import { getCoreUtils, nz } from "../../../Admin/kReusableCoreTS/kCoreHandler";
import { AppNodeOf } from "./AppNodeOf";
import { AppContact, AppIndex, AppRow } from "./AppIndex";
import { AppTaskRules } from "./AppTaskRules";
import { AppTaskView } from "./AppTaskView";
import { AppTaskAdd, AppTaskApi } from "./AppTaskApi";
import { AppTaskActions, AppTaskDecidedTell, IAppTaskDecided, IAppTaskHeard } from "./AppTaskActions";
import { AppViews } from "./AppViews";
import { AppFocusKeep, AppFocusMark, IFocusDoc, IFocusEl } from "./AppFocus";

// AppTaskBoard - the Tasks tab and the Today strip, live: ticking, snoozing, dismissing, reopening,
// adding, filtering and searching. `AppTaskRules` decides, `AppTaskView` draws, `AppTaskActions`
// sends; this is the act.
//
// ⛔ THE SWITCH IS HONOURED HERE TOO: for an index without the new fields (`AppTaskRules.Enabled`
// false) `TabHtml` IS `AppViews.Tasks` - byte for byte today's view - `StripHtml` is "", and
// neither `Wire` attaches a listener.
// ⚠ OPTIMISTIC, AND HONEST ABOUT IT: a tick moves the row at once (faded while saving); the
// service's answer settles it; a refusal puts the row back EXACTLY (a copy taken before the change)
// and shows the service's own sentence in ink under the search box - never the error handler.
// ⚠ ONE REQUEST IN FLIGHT PER TASK (`AppTaskActions`); a second tick is refused with a sentence.
// ⚠ SHAPED BY INTERFACES, so the suite drives it with a small fake element tree.

export interface IAppTaskEl {
    value?: string;
    innerHTML: string;
    textContent: string | null;
    className: string;
    getAttribute(Name: string): string | null;
    removeAttribute(Name: string): void;
    closest(Selector: string): IAppTaskEl | null;
    querySelector(Selector: string): IAppTaskEl | null;
    addEventListener(Type: string, Listener: (Ev: Event) => void): void;
}

/** The parts of a DOM event this reads - a real `Event` fits by shape. */
export interface IAppTaskEvent {
    target: unknown;
    preventDefault(): void;
}

/** What the board asks of the page around it. */
export interface IAppTaskBoardHost {
    /** The clock - "today" is the browser's day of this instant (`AppTaskRules.TodayLocal`). */
    Now(): Date;
    /** Open the task's chat with the AI panel asked "Help me reply about: ...". ⛔ Never sends. */
    ReplyAbout(Row: AppRow): void;
    /** The Tasks tab's number changed (a tick, a snooze, an add) - write it into the tab in place. */
    Counted(Count: string): void;
    /** A reply-owed row was opened: focus the message box once the chat `Href` is on screen. ⛔ Never sends. */
    FocusReply(Href: string): void;
}

export class AppTaskBoard {
    /** Which tasks the tab shows: mine | theirs | all. */
    public Who: string = "mine";
    public Search: string = "";
    private Index: AppIndex;
    private Actions: AppTaskActions;
    private Host: IAppTaskBoardHost;
    private Root: IAppTaskEl | null = null;
    private Mode: string = "";
    /** The server ids with a change on its way - their rows are faded, their ticks disabled. */
    private Pending: Record<string, boolean> = {};
    /** The folded sections ("Older") the owner has opened - kept across re-renders, forgotten on a new board. */
    private Open: Record<string, boolean> = {};
    /** Told when the service CONFIRMS a close or a reopen (the "Let <name> know?" card); null = nobody. */
    public Decided: IAppTaskDecided | null = null;

    public constructor(Index: AppIndex, Actions: AppTaskActions, Host: IAppTaskBoardHost) {
        this.Index = Index;
        this.Actions = Actions;
        this.Host = Host;
    }

    public Today = (): string => {
        if (getCoreUtils().KErrorMode === true) { return ""; }
        try {
            return AppTaskRules.TodayLocal(this.Host.Now());
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppTaskBoard.Today");
            return "";
        }
    };

    /** The browser's UTC offset now (`AppTaskRules.OffsetOf`) - the reply-age rule reads days in it. */
    public Offset = (): number => {
        if (getCoreUtils().KErrorMode === true) { return 0; }
        try {
            return AppTaskRules.OffsetOf(this.Host.Now());
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppTaskBoard.Offset");
            return 0;
        }
    };

    // ── Markup ───────────────────────────────────────────────────────────────────────────────

    /** The Tasks tab body. ⛔ Old index: exactly `AppViews.Tasks(Index)`. */
    public TabHtml = (): string => {
        if (getCoreUtils().KErrorMode === true) { return ""; }
        try {
            if (AppTaskRules.Enabled(this.Index) === false) { return AppViews.Tasks(this.Index); }
            return AppTaskView.Tab(this.Index, this.Today(), this.Offset(), this.Who, this.Search, this.Pending,
                this.Open);
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppTaskBoard.TabHtml");
            return "";
        }
    };

    /** The Today strip for the chats screen, or "" (old index, or nothing due). */
    public StripHtml = (): string => {
        if (getCoreUtils().KErrorMode === true) { return ""; }
        try {
            if (AppTaskRules.Enabled(this.Index) === false) { return ""; }
            return AppTaskView.Strip(this.Index, this.Today(), this.Offset(), this.Pending, false);
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppTaskBoard.StripHtml");
            return "";
        }
    };

    // ── Wiring (after every paint - the elements are new each time) ──────────────────────────

    /** `Root` is the `AppTaskView.TabId` section. Nothing is attached for an old index. */
    public WireTab = (Root: IAppTaskEl | null): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            if (Root === null || AppTaskRules.Enabled(this.Index) === false) { return; }
            this.Root = Root;
            this.Mode = "tab";
            Root.addEventListener("click", this.OnClick);
            const Box: IAppTaskEl | null = Root.querySelector("#" + AppTaskView.SearchId);
            if (Box !== null) { Box.addEventListener("input", this.OnSearch); }
            const Form: IAppTaskEl | null = Root.querySelector("#" + AppTaskView.AddFormId);
            if (Form !== null) { Form.addEventListener("submit", this.OnAdd); }
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppTaskBoard.WireTab");
        }
    };

    /** `Root` is the `AppTaskView.StripId` section, or null when no strip was drawn. */
    public WireStrip = (Root: IAppTaskEl | null): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            if (Root === null || AppTaskRules.Enabled(this.Index) === false) { return; }
            this.Root = Root;
            this.Mode = "strip";
            Root.addEventListener("click", this.OnClick);
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppTaskBoard.WireStrip");
        }
    };

    // ── Events ───────────────────────────────────────────────────────────────────────────────

    /** A click: a filter chip, or a task action button. Everything else is left to the browser. */
    public OnClick = (Ev: Event): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            const Node: IAppTaskEl | null = AppTaskBoard.NodeOf((Ev as unknown as IAppTaskEvent).target);
            if (Node === null) { return; }
            const Filter: IAppTaskEl | null = Node.closest("[" + AppTaskView.FilterAttr + "]");
            if (Filter !== null) { this.SetWho(nz(Filter.getAttribute(AppTaskView.FilterAttr))); return; }
            // ⚠ A FOLDED SECTION'S SUMMARY: the browser opens/closes the <details> itself (no preventDefault);
            // the board only remembers it, so the next re-render (a tick) draws it the same way.
            const Fold: IAppTaskEl | null = Node.closest("[" + AppTaskView.FoldAttr + "]");
            if (Fold !== null) { this.Flip(nz(Fold.getAttribute(AppTaskView.FoldAttr))); return; }
            const Button: IAppTaskEl | null = Node.closest("[" + AppTaskView.ActAttr + "]");
            if (Button === null) { this.Opened(Node); return; }
            (Ev as unknown as IAppTaskEvent).preventDefault();
            const Act: string = nz(Button.getAttribute(AppTaskView.ActAttr));
            const Id: string = nz(Button.getAttribute(AppTaskView.IdAttr));
            if (Act === "pickdate") { this.Act(Id, "snooze", AppTaskBoard.PickedDay(Button)); return; }
            if (Act === "reply") { this.Reply(Id); return; }
            this.Act(Id, Act, nz(Button.getAttribute(AppTaskView.UntilAttr)));
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppTaskBoard.OnClick");
        }
    };

    /**
     * A click on a row's own link (`AppTaskView.OpenLink` - anywhere on the row but its controls):
     * the BROWSER follows the link (no `preventDefault`); a reply owed also asks the host to focus
     * the message box when the chat opens. ⛔ Navigation only - nothing is sent or queued.
     */
    private Opened = (Node: IAppTaskEl): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            const Link: IAppTaskEl | null = Node.closest("[" + AppTaskView.OpenAttr + "]");
            if (Link === null) { return; }
            if (Link.getAttribute(AppTaskView.OpenReplyAttr) !== "1") { return; }
            this.Host.FocusReply(nz(Link.getAttribute(AppTaskView.OpenAttr)));
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppTaskBoard.Opened");
        }
    };

    /** ⚠ ONLY THE LIST IS RE-RENDERED - the box keeps its focus and caret. */
    public OnSearch = (Ev: Event): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            const Box: IAppTaskEl | null = AppTaskBoard.NodeOf((Ev as unknown as IAppTaskEvent).target);
            if (Box === null) { return; }
            this.Search = nz(Box.value);
            this.Refresh();
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppTaskBoard.OnSearch");
        }
    };

    /** A folded section was opened or closed by the owner. */
    public Flip = (Key: string): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            if (AppTaskRules.Folded.indexOf(Key) < 0) { return; }
            this.Open[Key] = this.Open[Key] !== true;
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppTaskBoard.Flip");
        }
    };

    /** Is the folded section `Key` open? For the suite, and for nothing else. */
    public IsOpen = (Key: string): boolean => {
        if (getCoreUtils().KErrorMode === true) { return false; }
        try {
            return this.Open[Key] === true;
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppTaskBoard.IsOpen");
            return false;
        }
    };

    public SetWho = (Who: string): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            if (AppTaskRules.Filters.indexOf(Who) < 0) { return; }
            this.Who = Who;
            this.Refresh();
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppTaskBoard.SetWho");
        }
    };

    // ── Changing a task ──────────────────────────────────────────────────────────────────────

    /**
     * Tick, snooze, dismiss or reopen the task `Id`: changed on screen at once, then sent; a refusal
     * puts it back. ⚠ Only a click reaches this - nothing changes a task on render or on a timer.
     */
    public Act = (Id: string, Action: string, Until: string): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            const Row: AppRow | null = AppTaskRules.FindById(this.Index, Id);
            if (Row === null) { return; }
            if (Action === "snooze") {
                const Bad: string = AppTaskRules.SnoozeRefusal(Until, this.Today());
                if (Bad.length > 0) { this.Say(Bad); return; }
            }
            const Before: AppRow = AppTaskRules.Copy(Row);
            AppTaskRules.Apply(Row, Action, Until, this.Host.Now().toISOString());
            const Why: string = this.Actions.Send(Row.Href, Action, Id, Until, null,
                new AppTaskBoardHeard(this, Row, Before, Action, Until, false));
            if (Why.length > 0) { AppTaskRules.Merge(Row, Before); this.Say(Why); return; }
            this.Pending[Id] = true;
            this.Say("");
            this.Refresh();
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppTaskBoard.Act");
        }
    };

    /** "Reply about this" - the chat, with the AI panel asked. Sends nothing. */
    public Reply = (Id: string): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            const Row: AppRow | null = AppTaskRules.FindById(this.Index, Id);
            if (Row === null) { return; }
            this.Host.ReplyAbout(Row);
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppTaskBoard.Reply");
        }
    };

    /** "+ Add task" submitted: validated, drawn at once (faded), then sent. */
    public OnAdd = (Ev: Event): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            (Ev as unknown as IAppTaskEvent).preventDefault();
            const Add: AppTaskAdd = new AppTaskAdd();
            Add.Label = this.Field("-l").trim();
            Add.Value = this.Field("-v").trim();
            Add.Due = this.Field("-d").trim();
            Add.Owner = this.Field("-o");
            const Href: string = this.Field("-c");
            const Who: AppContact | null = AppTaskBoard.ContactOf(this.Index, Href);
            const Why: string = AppTaskBoard.AddRefusal(Add, Who, this.Today());
            if (Why.length > 0) { this.Say(Why); return; }
            this.Add(Add, Who as AppContact);
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppTaskBoard.OnAdd");
        }
    };

    public Add = (Add: AppTaskAdd, Who: AppContact): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            const Row: AppRow = new AppRow();
            Row.Label = Add.Label; Row.Value = Add.Value; Row.Due = Add.Due; Row.Owner = Add.Owner;
            Row.Type = "task"; Row.Kind = "task"; Row.State = "open"; Row.Source = "you";
            Row.Contact = Who.Title; Row.Href = Who.Href; Row.PersonKey = Who.PersonKey;
            Row.Date = this.Today();
            // ⭐ THE PAGE MINTS THE `u-` ID (the contract allows it): a retried press adds the task
            // once, not twice, and the row can be ticked the moment the service has it.
            Row.Id = AppTaskBoard.NewUserId();
            const Why: string = this.Actions.Send(Who.Href, "add", Row.Id, "", Add,
                new AppTaskBoardHeard(this, Row, AppTaskRules.Copy(Row), "add", "", true));
            if (Why.length > 0) { this.Say(Why); return; }
            this.Pending[Row.Id] = true;
            this.Index.Tasks.push(Row);
            this.Say("");
            this.ClearAdd();
            this.Refresh();
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppTaskBoard.Add");
        }
    };

    /** The service took the change. `Row` already holds the server's version (`Adopt`). */
    public Landed = (Row: AppRow, Action: string, Until: string, Changed: boolean = true): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            delete this.Pending[Row.Id];
            this.Refresh();
            this.Say(AppTaskRules.DoneWord(Action, Until));
            // ⛔ ONLY NOW - THE SERVICE HAS IT. The optimistic tick offers nothing; a refused one never will.
            AppTaskDecidedTell.Tell(this.Decided, Row, Action, Changed);
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppTaskBoard.Landed");
        }
    };

    /** Refused or lost: the row is already put back; show the service's sentence as-is. */
    public Refused = (Row: AppRow, Added: boolean, Why: string): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            delete this.Pending[Row.Id];
            if (Added === true) {
                const At: number = this.Index.Tasks.indexOf(Row);
                if (At >= 0) { this.Index.Tasks.splice(At, 1); }
            }
            this.Refresh();
            this.Say(Why);
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppTaskBoard.Refused");
        }
    };

    /** Is a change to `Id` on its way (its row faded)? For the suite, and for nothing else. */
    public IsPending = (Id: string): boolean => {
        if (getCoreUtils().KErrorMode === true) { return false; }
        try {
            return this.Pending[Id] === true;
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppTaskBoard.IsPending");
            return false;
        }
    };

    // ── Drawing ──────────────────────────────────────────────────────────────────────────────

    /** Re-render the list part of whichever view is on screen. */
    public Refresh = (): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            if (this.Root === null) { return; }
            const Root: IAppTaskEl = this.Root as IAppTaskEl;
            const Today: string = this.Today();
            const Offset: number = this.Offset();
            // ⚠ THE TAB'S NUMBER FOLLOWS AT ONCE - a tick that leaves "Tasks 7" standing reads as not taken.
            this.Host.Counted(AppTaskRules.TabCount(this.Index, Today));
            // ⭐ THE KEYBOARD'S PLACE SURVIVES THE REDRAW (`AppFocusKeep`): a tick or a filter pressed with Enter used to
            // leave the focus on nothing - the list it was in had just been replaced.
            const Kept: AppFocusMark = AppFocusKeep.Before(AppTaskBoard.DocOf(Root), Root as unknown as IFocusEl);
            if (this.Mode === "tab") {
                const List: IAppTaskEl | null = Root.querySelector("#" + AppTaskView.ListId);
                if (List !== null) { List.innerHTML = AppTaskView.List(this.Index, Today, Offset, this.Who, this.Search, this.Pending, this.Open); }
                AppFocusKeep.After(Root as unknown as IFocusEl, Kept);
                return;
            }
            const Strip: IAppTaskEl | null = Root.querySelector("#" + AppTaskView.StripListId);
            if (Strip !== null) {
                Strip.innerHTML = AppTaskView.StripList(AppTaskRules.Strip(this.Index, Today, Offset), Today, Offset, this.Pending);
            }
            AppFocusKeep.After(Root as unknown as IFocusEl, Kept);
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppTaskBoard.Refresh");
        }
    };

    /** The line under the search box (or the strip). ⛔ `textContent` - a refusal's words are the service's. */
    public Say = (Text: string): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            if (this.Root === null) { return; }
            let Id: string = AppTaskView.SayId;
            if (this.Mode === "strip") { Id = AppTaskView.StripSayId; }
            const Line: IAppTaskEl | null = (this.Root as IAppTaskEl).querySelector("#" + Id);
            if (Line !== null) { Line.textContent = nz(Text); }
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppTaskBoard.Say");
        }
    };

    // ── Small helpers ────────────────────────────────────────────────────────────────────────

    /** The document `Root` lives in (for "what has the focus"), or null for a test's bare element. */
    private static DocOf = (Root: IAppTaskEl): IFocusDoc | null => {
        if (getCoreUtils().KErrorMode === true) { return null; }
        try {
            const Doc: unknown = (Root as unknown as { ownerDocument?: unknown }).ownerDocument;
            if (Doc === null || Doc === undefined) { return null; }
            return Doc as IFocusDoc;
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppTaskBoard.DocOf");
            return null;
        }
    };

    /** The new-task row's box with this suffix (`-l`, `-v`, `-d`, `-o`, `-c`), or null (no root, or not drawn). */
    private AddBox = (Suffix: string): IAppTaskEl | null => {
        if (getCoreUtils().KErrorMode === true) { return null; }
        try {
            if (this.Root === null) { return null; }
            return (this.Root as IAppTaskEl).querySelector("#" + AppTaskView.AddFormId + Suffix);
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppTaskBoard.AddBox");
            return null;
        }
    };

    /** The value of an add-form field by suffix (`-l`, `-v`, `-d`, `-o`, `-c`), or "". */
    private Field = (Suffix: string): string => {
        if (getCoreUtils().KErrorMode === true) { return ""; }
        try {
            const Box: IAppTaskEl | null = this.AddBox(Suffix);
            if (Box === null) { return ""; }
            return nz(Box.value);
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppTaskBoard.Field");
            return "";
        }
    };

    private ClearAdd = (): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            for (const Suffix of ["-l", "-v", "-d"]) {
                const Box: IAppTaskEl | null = this.AddBox(Suffix);
                if (Box !== null) { Box.value = ""; }
            }
            // ⭐ The new-task row stays where it is (it is part of the list), empty and ready for the next one.
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppTaskBoard.ClearAdd");
        }
    };

    /** Validation for "+ Add task" - "" when it may go. ⚠ Ink sentences, never the handler. */
    public static AddRefusal = (Add: AppTaskAdd, Who: AppContact | null, Today: string): string => {
        if (getCoreUtils().KErrorMode === true) { return ""; }
        try {
            if (nz(Add.Label).trim().length === 0) { return "Give the task a name."; }
            if (Who === null) { return "Choose who the task is with."; }
            if (nz(Add.Due).length > 0 && AppTaskRules.IsDay(Add.Due) === false) { return "That due date isn't a day."; }
            if (nz(Add.Due).length > 0 && Add.Due < Today) { return "Pick today or a later day."; }
            return "";
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppTaskBoard.AddRefusal");
            return "";
        }
    };

    /** A fresh `u-` + 12 hex id (`AppTaskApi.UserIdPattern`), from the platform's random source. */
    public static NewUserId = (): string => {
        if (getCoreUtils().KErrorMode === true) { return ""; }
        try {
            return AppTaskApi.NewUserId(false, "AppTaskBoard.NewUserId");
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppTaskBoard.NewUserId");
            return "";
        }
    };

    /** The 1:1 contact with this href, or null. ⛔ Compared in a loop. */
    public static ContactOf = (Index: AppIndex, Href: string): AppContact | null => {
        if (getCoreUtils().KErrorMode === true) { return null; }
        try {
            if (nz(Href).length === 0) { return null; }
            for (const One of Index.Contacts) {
                if (One.Href === Href && One.IsGroup === false) { return One; }
            }
            return null;
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppTaskBoard.ContactOf");
            return null;
        }
    };

    /** The day typed in the menu's date box beside a "Snooze" button, or "". */
    private static PickedDay = (Button: IAppTaskEl): string => {
        if (getCoreUtils().KErrorMode === true) { return ""; }
        try {
            const Pick: IAppTaskEl | null = Button.closest(".tpick");
            if (Pick === null) { return ""; }
            const Box: IAppTaskEl | null = Pick.querySelector("input");
            if (Box === null) { return ""; }
            return nz(Box.value);
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppTaskBoard.PickedDay");
            return "";
        }
    };

    /**
     * An event target as an element that can `closest`, or null (a text node, the document).
     * DOCUMENTED EXCEPTION: a type probe over a value in hand.
     * ERROR-SUPPRESSED-JUSTIFIED: null IS the answer for anything that is not an element.
     */
    private static NodeOf = (Target: unknown): IAppTaskEl | null => {
        return AppNodeOf.Read<IAppTaskEl>(Target);
    };
}

/** Hears how ONE board change went (`IAppTaskHeard`). A class, not a closure. */
export class AppTaskBoardHeard implements IAppTaskHeard {
    private Board: AppTaskBoard;
    private Row: AppRow;
    private Before: AppRow;
    private Action: string;
    private Until: string;
    private Added: boolean;

    public constructor(Board: AppTaskBoard, Row: AppRow, Before: AppRow, Action: string, Until: string, Added: boolean) {
        this.Board = Board;
        this.Row = Row;
        this.Before = Before;
        this.Action = Action;
        this.Until = Until;
        this.Added = Added;
    }

    public Done = (Task: AppRow | null, Changed: boolean = true): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            if (Task !== null) { AppTaskRules.Adopt(this.Row, Task as AppRow); }
            this.Board.Landed(this.Row, this.Action, this.Until, Changed);
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppTaskBoardHeard.Done");
        }
    };

    /** ⚠ THE ROW IS PUT BACK EXACTLY - every field from the copy taken before the change. */
    public Failed = (Why: string): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            AppTaskRules.Merge(this.Row, this.Before);
            this.Board.Refused(this.Row, this.Added, Why);
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppTaskBoardHeard.Failed");
        }
    };
}
